import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError } from 'rxjs/internal/operators/catchError';
import { ToastService } from './toast';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { ChangeLangService } from './change-lang';
import { SessionService } from './session';
import { SessionExpiryService } from './session-expiry';
import { environment } from '../../../environments/environment.dev';

/**
 * POST endpoints still permitted while the account is deactivated — i.e. the
 * license-reactivation flow plus session control.
 */
const ALLOWED_WHILE_DEACTIVATED = [
  'functions/refreshLicense',
  'functions/updateRegistrationDetails',
  'functions/login',
  'functions/logout',
];

/**
 * Endpoints that work without a session: the login screen and the whole
 * self-registration flow. They never carry the session token, they are never
 * blocked once the session has expired, and their failures are never treated as
 * an expiry (a wrong password must not read as a dead session).
 */
const UNAUTHENTICATED_ENDPOINTS = [
  'functions/loginUser',
  'functions/generateOTP',
  'functions/verifyOTP',
  'functions/resetPassword',
  'functions/getDLDBrokerData',
  'functions/getDLDAgencyData',
  'functions/getDLDDeveloperData',
  'functions/brokerSignUp',
  'functions/agencySignUp',
  'functions/developerSignUp',
];

/**
 * Logout still carries the token (the server needs it to invalidate the
 * session), but a rejection there is the expected outcome of leaving an already
 * dead session — not an expiry worth announcing. The caller clears the session
 * locally either way.
 */
const SESSION_TEARDOWN_ENDPOINT = 'functions/logout';

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  apiKey = environment.clientKey;
  appId = environment.appId;

  toastService = inject(ToastService);
  router = inject(Router);
  dialogService = inject(DialogService);
  langService = inject(ChangeLangService);
  sessionService = inject(SessionService);
  sessionExpiry = inject(SessionExpiryService);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Once the session is known to be dead, stop putting the expired token back
    // on the wire: fail fast locally instead of firing more doomed requests
    // while the redirect to /login is still in flight.
    if (this.isBlockedBySessionExpiry(req)) {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: { code: 209, error: 'Invalid session token' },
            status: 401,
            statusText: 'Unauthorized',
            url: req.url,
          })
      );
    }

    // Block every POST except the license-reactivation flow while the account
    // is deactivated (accountStatus code '2'), instead of letting it round-trip
    // to the server and come back as a raw 403.
    if (this.isBlockedWhileDeactivated(req)) {
      const message = this.sessionService.inactiveMessage();
      this.toastService.showToast('warn', message, '');
      // Shaped like a real HttpErrorResponse so callers that inspect
      // `err.error?.error` behave the same whether the block happened here
      // or on the server.
      return throwError(
        () =>
          new HttpErrorResponse({
            error: { error: message },
            status: 403,
            statusText: 'Forbidden',
            url: req.url,
          })
      );
    }

    const setHeaders: any = {
      'X-Parse-REST-API-Key': this.apiKey,
      'X-Parse-Application-Id': this.appId,
    };

    const sessionToken = this.sessionService.getSessionToken();
    // Only a request that actually carried a token can come back as an expiry —
    // an unauthorized call made with no session at all (e.g. the FCM
    // registration firing on the login screen) is a different problem.
    const carriesSession = !!sessionToken && this.isSessionBackedRequest(req);
    if (carriesSession) {
      setHeaders['X-Parse-Session-Token'] = sessionToken;
    }

    const clonedRequest = req.clone({
      setHeaders,
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        // Preserve the original error (headers/body/status) for downstream
        // subscribers — we only ever read from it here, never replace it.

        // An expired/revoked token: log out and redirect instead of leaving the
        // user on a screen that can only produce more unauthorized requests.
        // No generic toast — the login screen explains what happened.
        if (carriesSession && this.sessionExpiry.isSessionExpiredError(error)) {
          if (!req.url.includes(SESSION_TEARDOWN_ENDPOINT)) {
            this.sessionExpiry.handleExpiredSession();
          }
          return throwError(() => error);
        }

        if (error.status === 403 && this.handleInactiveAccountError(error)) {
          return throwError(() => error);
        }

        const errorMessage = this.getErrorMessage(error);
        this.toastService.showToast('error', errorMessage, '');
        return throwError(() => error);
      })
    );
  }

  /** True for calls to the backend API (i18n assets and the like are exempt). */
  private isApiRequest(req: HttpRequest<any>): boolean {
    return req.url.startsWith(environment.baseURL);
  }

  /** True for the endpoints that are reachable without a session. */
  private isUnauthenticatedEndpoint(req: HttpRequest<any>): boolean {
    return UNAUTHENTICATED_ENDPOINTS.some((path) => req.url.includes(path));
  }

  /** True when the request carries the session token, and can therefore expire. */
  private isSessionBackedRequest(req: HttpRequest<any>): boolean {
    return this.isApiRequest(req) && !this.isUnauthenticatedEndpoint(req);
  }

  /** True when a request must not be sent because the session already expired. */
  private isBlockedBySessionExpiry(req: HttpRequest<any>): boolean {
    return this.sessionExpiry.isExpired() && this.isSessionBackedRequest(req);
  }

  /** True when a POST must be blocked because the account is deactivated. */
  private isBlockedWhileDeactivated(req: HttpRequest<any>): boolean {
    if (req.method !== 'POST') return false;
    if (!this.sessionService.isAccountInactive()) return false;
    return !ALLOWED_WHILE_DEACTIVATED.some((path) => req.url.includes(path));
  }

  /**
   * When a 403 carries an inactive-account payload, sync the locally-cached
   * account status (so the pre-emptive guard above takes over on the next
   * request instead of round-tripping to the server again) and surface the
   * backend's specific message instead of a generic "Forbidden" toast.
   * Returns true when this 403 was handled as an inactive-account error.
   */
  private handleInactiveAccountError(error: HttpErrorResponse): boolean {
    const body = error.error;
    if (!body || typeof body !== 'object') return false;

    const accountStatus =
      body.accountStatus ?? body.data?.accountStatus ?? body.user?.accountStatus;
    const rawMessage = body.error ?? body.message;
    const inactiveKeyword = /inactive|deactivat|license/i;
    const looksInactive =
      !!accountStatus ||
      (typeof rawMessage === 'string' && inactiveKeyword.test(rawMessage)) ||
      (rawMessage &&
        typeof rawMessage === 'object' &&
        inactiveKeyword.test(rawMessage.en || rawMessage.ar || ''));

    if (!looksInactive) return false;

    if (accountStatus) {
      this.sessionService.setAccountStatus(accountStatus);
    }

    const message = this.extractLocalizedMessage(rawMessage) ?? this.sessionService.inactiveMessage();
    this.toastService.showToast('warn', message, '');
    return true;
  }

  private extractLocalizedMessage(raw: any): string | null {
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
      const currentLang = this.langService.currentLang() || 'en';
      return raw[currentLang] || raw.en || raw.ar || null;
    }
    return null;
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    const errorData = error.error?.error;

    // Check if error is multilingual object { en: "...", ar: "..." }
    if (errorData && typeof errorData === 'object' && (errorData.en || errorData.ar)) {
      const currentLang = this.langService.currentLang() || 'en';
      return errorData[currentLang] || errorData.en || errorData.ar || 'An unexpected error occurred.';
    }

    // If error is a plain string
    if (typeof errorData === 'string') {
      return errorData;
    }

    // Fallback
    return error.statusText || 'An unexpected error occurred.';
  }
}
